/**
 * THE FASTFRAME MARK — the one place its geometry exists.
 *
 * Measured 19.09.2026 out of Figma "My Assets" (file KmkipTr9bIM6N7hJDeLD8W,
 * node 85:7272) and re-measured 20.09.2026 off the exported SVG: SIXTEEN
 * rounded squares (not seventeen), 4.94118 wide, 1 unit corner radius, on a
 * 5x5 lattice inside a 40x40 frame with a 6-unit margin — so the glyph's own
 * box is exactly `6 6 28 28`.
 *
 * The pattern is the "#": a lattice cell is inked when its row OR its column
 * is odd, which leaves the four corners and the four "quiet" cells empty. That
 * makes the mark symmetric under a 90 degree rotation (asserted in
 * brand-mark.test.ts), which is why it can be used as a spinner without
 * looking like a logo falling over.
 *
 * THE COORDINATES ARE THE FIGMA NUMBERS, NOT A COMPUTED GRID. A parametric
 * step of 5.76465 reproduces them to within 0.0003 and would be prettier; it
 * would also be a redrawing of somebody else's file. What is checked in is what
 * was exported.
 *
 * This module is framework-free on purpose: `BrandMark.tsx` renders it in
 * React for apps/web and apps/admin, and `brandMarkSvgMarkup()` emits the same
 * geometry as a standalone file for the favicon / manifest / mail generator.
 * Nothing else may hold a copy of these numbers — `brand-mark-has-one-home.test.ts`
 * in apps/web fails if a second one appears.
 */

/** The glyph's own box. Not the 40x40 Figma frame — the frame's 6-unit margin
 *  is layout around the mark, and a caller that wants it adds its own padding. */
export const BRAND_MARK_VIEW_BOX = '6 6 28 28';

/** Column origins (x), left to right, straight out of the export. */
export const BRAND_MARK_COLUMNS = [6.00012, 11.7657, 17.5294, 23.295, 29.0587] as const;

/** Row origins (y), top to bottom. */
export const BRAND_MARK_ROWS = [6, 11.7646, 17.5294, 23.2942, 29.0588] as const;

/** Side of one square. */
export const BRAND_MARK_SQUARE = 4.94118;

/** Corner radius of one square. */
export const BRAND_MARK_RADIUS = 1;

/** A lattice cell is inked when its row or its column is odd. */
export function brandMarkCellIsInked(row: number, column: number): boolean {
  return row % 2 === 1 || column % 2 === 1;
}

export interface BrandMarkSquare {
  readonly x: number;
  readonly y: number;
}

/** The sixteen squares, in row-major order. */
export const BRAND_MARK_SQUARES: readonly BrandMarkSquare[] = BRAND_MARK_ROWS.flatMap((y, row) =>
  BRAND_MARK_COLUMNS.flatMap((x, column) => (brandMarkCellIsInked(row, column) ? [{ x, y }] : [])),
);

/**
 * The mark as a standalone SVG document.
 *
 * `fill` defaults to `currentColor` so the string can be inlined into a page
 * and inherit a token; the favicon generator passes a concrete ink because a
 * file loaded through `<link rel=icon>` inherits nothing. `background` paints a
 * full-bleed plate under the glyph — iOS composites `apple-touch-icon` onto the
 * home screen with no ground of its own, so that one cannot be transparent.
 */
export function brandMarkSvgMarkup(options: {
  readonly size?: number;
  readonly fill?: string;
  readonly darkFill?: string;
  readonly background?: string;
  readonly darkBackground?: string;
  readonly title?: string;
  readonly padding?: number;
} = {}): string {
  const {
    size = 28,
    fill = 'currentColor',
    darkFill,
    background,
    darkBackground,
    title,
    padding = 0,
  } = options;
  const [vx, vy, vw, vh] = BRAND_MARK_VIEW_BOX.split(' ').map(Number);
  const box = `${vx - padding} ${vy - padding} ${vw + padding * 2} ${vh + padding * 2}`;
  // KNOCKOUT: the plate is themed too, so the mark is a hole in a solid tile in
  // BOTH schemes rather than a scatter of squares. That is the admin cut — see
  // `brandAdminFaviconSvg` for why the difference has to be the silhouette.
  const knockout = Boolean(background && darkBackground && darkFill);
  const plate = background
    ? `\n  <rect x="${vx - padding}" y="${vy - padding}" width="${vw + padding * 2}" height="${vh + padding * 2}"${knockout ? ` class="p" fill="${background}"` : ` fill="${background}"`}/>`
    : '';
  const label = title ? `\n  <title>${title}</title>` : '';
  // A file loaded through `<link rel=icon>` inherits nothing from the page, so
  // a themed favicon has to carry its own query. Chrome and Firefox honour a
  // `prefers-color-scheme` rule inside the SVG; Safari ignores it and keeps the
  // light ink, which is the correct degradation (its tab strip is light).
  const themed = darkFill
    ? `\n  <style>.m{fill:${fill}}${knockout ? `.p{fill:${background}}` : ''}@media (prefers-color-scheme:dark){.m{fill:${darkFill}}${knockout ? `.p{fill:${darkBackground}}` : ''}}</style>`
    : '';
  // The class exists only so the media query can reach the squares WITHOUT
  // reaching the background plate, which is also a <rect>.
  // In knockout mode every rect ALSO carries its light-scheme `fill` attribute:
  // a CSS rule beats a presentation attribute, so the media query still wins,
  // and a renderer that ignores the <style> gets the light cut instead of ink
  // squares on an ink plate, i.e. an empty tile.
  const ink = knockout ? ` class="m" fill="${fill}"` : darkFill ? ' class="m"' : ` fill="${fill}"`;
  // ROOT FILL IS THE FALLBACK, AND IT MATTERS. `fill` inherits, so a root
  // `fill="none"` above class-only squares means an invisible mark the moment
  // the <style> does not apply — and a favicon is fetched as an image, in a
  // mode whose CSS support is not something this file gets to assume. With the
  // light ink on the root, losing the stylesheet costs the dark cut, not the
  // icon. Without `darkFill` the squares carry their own fill and the root
  // stays `none`, which is what every SVG export does.
  const rootFill = knockout ? 'none' : darkFill ? fill : 'none';
  const squares = BRAND_MARK_SQUARES.map(
    ({ x, y }) =>
      `\n  <rect x="${x}" y="${y}" width="${BRAND_MARK_SQUARE}" height="${BRAND_MARK_SQUARE}" rx="${BRAND_MARK_RADIUS}"${ink}/>`,
  ).join('');
  return `<svg width="${size}" height="${size}" viewBox="${box}" fill="${rootFill}" xmlns="http://www.w3.org/2000/svg">${label}${themed}${plate}${squares}\n</svg>\n`;
}

/** DS `icon/primary` — the mark's ink, and the plate an app icon sits on. */
export const BRAND_MARK_INK = '#0F1011';

/** The mark's cut for dark ground. */
export const BRAND_MARK_PAPER = '#FFFFFF';

/**
 * The exact bytes of `public/favicon.svg`, for both apps.
 *
 * It is a function rather than a checked-in file with a comment on top because
 * a comment does not fail: `brand-mark-has-one-home.test.ts` compares the files
 * on disk to what this returns, so a hand-edited favicon — or one left behind
 * when the geometry moves — goes red instead of shipping a mark that disagrees
 * with the app's.
 */
export function brandFaviconSvg(): string {
  return brandMarkSvgMarkup({
    size: 32,
    fill: BRAND_MARK_INK,
    darkFill: BRAND_MARK_PAPER,
    title: 'FastFrame',
  });
}

/**
 * Margin between the glyph and the edge of the admin plate, in viewBox units.
 *
 * 3 rather than the Figma frame's 6: the plate IS the icon here, so the glyph
 * has to fill it. At 6 the mark shrinks to nine of sixteen pixels at the 16px
 * rung and the tile reads as an empty dark square.
 */
export const BRAND_MARK_ADMIN_PLATE_PADDING = 3;

/**
 * The exact bytes of `apps/admin/public/favicon.svg` — the SAME mark, INVERTED.
 *
 * The owner's line, 21.09.2026: the console's tab must be impossible to
 * confuse with the product's, because both are pinned all day and the wrong
 * click is a support agent typing into the customer's workspace. Until that
 * day both files were byte-identical (1546 bytes), so the only difference was
 * the title — which is the first thing a browser truncates away.
 *
 * WHY THE INVERTED PLATE AND NOT A BADGE OR A HUE. Rendered at 16 and 32px on
 * both a light and a dark tab strip (the comparison is in the MR):
 *  - a corner dot survives as two or three pixels of colour and is gone the
 *    moment the tab is not the active one;
 *  - recolouring the mark to `brand` keeps the SILHOUETTE — a scatter of
 *    squares — so the two tabs differ only by hue, which is the one channel a
 *    colour-blind eye does not have and which the dark tab strip washes out
 *    anyway;
 *  - the knockout changes the silhouette itself: a solid tile against a
 *    scatter. That is legible at 16px, in both schemes, and at a glance.
 *
 * It flips with the scheme for the same reason the app's mark does, and the
 * flip is the WHOLE tile: an ink plate on a dark tab strip is very nearly the
 * strip's own colour, and the admin icon would collapse back into the app's
 * scatter of white squares. On dark the plate is paper and the mark is ink.
 */
export function brandAdminFaviconSvg(): string {
  return brandMarkSvgMarkup({
    size: 32,
    padding: BRAND_MARK_ADMIN_PLATE_PADDING,
    fill: BRAND_MARK_PAPER,
    darkFill: BRAND_MARK_INK,
    background: BRAND_MARK_INK,
    darkBackground: BRAND_MARK_PAPER,
    title: 'FastFrame Admin',
  });
}
